import React, { useCallback, useEffect, useRef } from "react";
import { Button } from "./Button";
import {
  generateMixMap,
  generateNoiseWithAI,
  MapEnum,
  MapType,
  generateNoiseV2,
  GeneratorOptions,
  ModifiersType,
  MapProperties,
  MapOptions,
  GeneratorProperties,
  GetNoiseFunctionType,
  availableTextures,
  TextureType,
  ProcessOptions,
  ProcessOptionsType,
  WaterOptions,
  rgbToHex,
  hexToRgb,
} from "../generators";
import { Range } from "./Range";
import styles from "./Options.module.css";
import { OptionsGroup } from "./OptionsGroup";
import cx from "classnames";
import { Select } from "./Select";

type PropsType = {
  setMapType: (type: MapType) => void;
  noiseCanvasRef: React.RefObject<HTMLCanvasElement>;
  mixMapCanvasRef: React.RefObject<HTMLCanvasElement>;
  mapType: MapType;
  redrawTerrain: () => void;
  loadImage: (evt: any) => void;
  clearNoise: () => void;
  getNoise: GetNoiseFunctionType;
  mapOptions: MapOptions;
  setMapOptions: (
    options: MapOptions | ((current: MapOptions) => MapOptions)
  ) => void;
  setMapSize: (size: number) => void;
  mapSize: number;
};

type Callback = (...args: any[]) => void;

const random = (min: number, max: number) =>
  Math.floor(min + Math.random() * (max - min));

function useThrottle(callback: Callback, delay: number): Callback {
  const lastCallRef = useRef<number | null>(null);
  const callbackRef = useRef<Callback>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    (...args: any[]) => {
      if (!lastCallRef.current) {
        lastCallRef.current = window.setTimeout(() => {
          callbackRef.current(...args);
          lastCallRef.current = null;
        }, delay);
      }
    },
    [delay]
  );
}

export function Options({
  setMapType,
  noiseCanvasRef,
  mapType,
  mixMapCanvasRef,
  redrawTerrain,
  loadImage,
  clearNoise,
  getNoise,
  mapOptions,
  setMapOptions,
  setMapSize,
  mapSize,
}: PropsType) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [defaultGenerateOptions, setDefaultGenerateOptions] = React.useState(
    GeneratorProperties[mapType]
  );

  const [autoGenerate, setAutoGenerate] = React.useState(true);

  const [generateOptions, setGenerateOptions] =
    React.useState<GeneratorOptions>(defaultGenerateOptions);

  const currentTextures = React.useMemo(
    () => mapOptions.textures.map((t) => t.name),
    [mapOptions.textures]
  );

  React.useEffect(() => {
    setDefaultGenerateOptions(GeneratorProperties[mapType]);
    setGenerateOptions(GeneratorProperties[mapType]);
  }, [mapType]);

  const handleGenerateOptionCheck = React.useCallback(
    (type: ModifiersType | ProcessOptionsType, e) => {
      setGenerateOptions((current) => ({
        ...current,
        [type]: e.target.checked,
      }));
    },
    []
  );

  const handleGeneratorRangeChange = React.useCallback((label, value) => {
    setGenerateOptions((current) => ({
      ...current,
      [label]: value,
    }));
  }, []);

  const handleGeneratorSelectChange = React.useCallback(
    (label: keyof (typeof GeneratorProperties)[MapType], value) => {
      setGenerateOptions((current) => ({
        ...current,
        [label]: value,
      }));
    },
    []
  );

  const handleVectorChange = React.useCallback(
    (vectorIndex: number, vectorProp: "x" | "y", value) => {
      setGenerateOptions((current) => {
        return {
          ...current,
          erosionVectors: current.erosionVectors.map((vector, index) => {
            if (index === vectorIndex) {
              return {
                ...vector,
                [vectorProp]: value,
              };
            }

            return vector;
          }),
        };
      });
    },
    []
  );

  const handleMapOptionCheck = React.useCallback(
    (type: keyof MapOptions, e) => {
      setMapOptions((current) => ({
        ...current,
        [type]: e.target.checked,
      }));
    },
    [setMapOptions]
  );

  const handleMapOptionRangeChange = React.useCallback(
    (label, value) => {
      setMapOptions((current) => ({
        ...current,
        [label]: value,
      }));
    },
    [setMapOptions]
  );

  const handleTextureUVChange = React.useCallback(
    (index, value) => {
      setMapOptions((options) => ({
        ...options,
        textures: options.textures.map((texture, idx) =>
          idx === index
            ? {
                ...texture,
                uv: value,
              }
            : texture
        ) as [TextureType, TextureType, TextureType, TextureType],
      }));
    },
    [setMapOptions]
  );

  const handleTextureLevelChange = React.useCallback(
    (index, value) => {
      setMapOptions((options) => {
        const newLevels = [...options.levels];
        newLevels[index] = value;
        return {
          ...options,
          levels: newLevels as [number, number, number],
        };
      });
    },
    [setMapOptions]
  );

  const handleTextureChange = React.useCallback(
    (index, value) => {
      setMapOptions((options) => ({
        ...options,
        textures: options.textures.map((texture, idx) =>
          idx === index
            ? {
                ...texture,
                name: value,
              }
            : texture
        ) as [TextureType, TextureType, TextureType, TextureType],
      }));
    },
    [setMapOptions]
  );

  const hadleWaterOptionChange = React.useCallback(
    (type: keyof WaterOptions, value) => {
      setMapOptions((options) => ({
        ...options,
        water: {
          ...options.water,
          [type]: value,
        },
      }));
    },
    [setMapOptions]
  );

  const setRandomTextures = React.useCallback(() => {
    setMapOptions((options) => {
      const pool = [...availableTextures];
      const randomTextures = new Array(4).fill(null).map<TextureType>(() => {
        const index = random(0, pool.length - 1);
        const name = pool[index];
        pool.splice(index, 1);
        console.log({ pool, index, name });
        return {
          name,
          uv: random(5, 20),
        };
      });

      let prevLevel = 0.1;

      const randomLevels = new Array(3).fill(null).map(() => {
        const level = Math.random() * (1 - prevLevel) + prevLevel;
        prevLevel = level;
        return level;
      });

      return {
        ...options,
        textures: randomTextures as [
          TextureType,
          TextureType,
          TextureType,
          TextureType
        ],
        // levels: randomLevels as [number, number, number],
      };
    });
  }, [setMapOptions]);

  const generate = React.useCallback(async () => {
    if (autoGenerate) {
      await generateNoiseV2(generateOptions, getNoise);
    }

    generateMixMap(
      mixMapCanvasRef.current,
      noiseCanvasRef.current,
      mapOptions.levels
    );

    redrawTerrain();
  }, [
    generateOptions,
    getNoise,
    mixMapCanvasRef,
    noiseCanvasRef,
    mapOptions.levels,
    redrawTerrain,
    autoGenerate,
  ]);

  const throttledGenerate = useThrottle(
    generate,
    generateOptions.multithreading ? 35 : 1
  );

  React.useEffect(() => {
    throttledGenerate();
  }, [
    autoGenerate,
    noiseCanvasRef,
    generateOptions,
    mixMapCanvasRef,
    getNoise,
    redrawTerrain,
    mapOptions.levels,
    throttledGenerate,
  ]);

  return (
    <div className={styles.options}>
      <div className={styles.generateOptions}>
        <Select
          label="Preset"
          onChange={(ev) => setMapType(ev.target.value as MapType)}
          values={Object.keys(MapEnum).map((key) => ({
            value: key,
            name: key,
          }))}
        />
        <div className={styles.optionCheck}>
          <label htmlFor="auto-generate">Auto generate</label>
          <input
            id="auto-generate"
            type="checkbox"
            checked={autoGenerate}
            onChange={(e) => setAutoGenerate(e.target.checked)}
          />
        </div>
        <div className={styles.optionCheck}>
          <label htmlFor="multithreading">Multithreading</label>
          <input
            id="multithreading"
            type="checkbox"
            checked={generateOptions.multithreading}
            onChange={(e) => handleGenerateOptionCheck("multithreading", e)}
          />
        </div>
        {!autoGenerate && (
          <Button type="primary" onClick={generate}>
            Generate
          </Button>
        )}
        <OptionsGroup label="Basic options" defaultOpen={true}>
          <Select
            label="Noise function"
            onChange={(ev) =>
              handleGeneratorSelectChange("noiseGenerator", ev.target.value)
            }
            values={[
              {
                name: "Simplex",
                value: "simplex",
              },
              {
                name: "Perlin",
                value: "perlin",
              },
            ]}
            value={generateOptions.noiseGenerator}
          />
          <div className="d-flex mb-2">
            <div className={styles.optionCheck}>
              <label htmlFor="absoluteNoise">Absolute noise</label>
              <input
                id="absoluteNoise"
                type="checkbox"
                checked={generateOptions.absoluteNoise}
                onChange={(e) => handleGenerateOptionCheck("absoluteNoise", e)}
              />
            </div>
            <div className={styles.optionCheck}>
              <label htmlFor="invertNoise">Invert noise</label>
              <input
                id="invertNoise"
                type="checkbox"
                checked={generateOptions.invertNoise}
                onChange={(e) => handleGenerateOptionCheck("invertNoise", e)}
              />
            </div>
          </div>
          <Range
            label="offsetX"
            value={generateOptions.offsetX}
            min={-2}
            max={2}
            step={0.01}
            onChange={handleGeneratorRangeChange}
          />
          <Range
            label="offsetY"
            value={generateOptions.offsetY}
            min={-2}
            max={2}
            step={0.01}
            onChange={handleGeneratorRangeChange}
          />
          <Range
            label="scale"
            value={generateOptions.scale}
            min={1}
            max={1000}
            step={10}
            onChange={handleGeneratorRangeChange}
          />
          <Range
            label="numOctaves"
            value={generateOptions.numOctaves}
            min={1}
            max={8}
            onChange={handleGeneratorRangeChange}
          />
          <Range
            label="seed"
            value={generateOptions.seed}
            max={100}
            min={1}
            onChange={handleGeneratorRangeChange}
          />
          <Range
            label="selfSimilarity"
            value={generateOptions.selfSimilarity}
            max={1}
            min={0}
            step={0.1}
            onChange={handleGeneratorRangeChange}
          />
        </OptionsGroup>
        <OptionsGroup label="Water">
          <div className={styles.optionCheck}>
            <label htmlFor="showWater">Show water</label>
            <input
              id="showWater"
              type="checkbox"
              checked={mapOptions.water.show}
              onChange={(e) => hadleWaterOptionChange("show", e.target.checked)}
            />
          </div>
          <Range
            label={`waterLevel`}
            value={mapOptions.water.level}
            max={mapOptions.heightScale / 2}
            min={-mapOptions.heightScale / 2}
            step={0.1}
            onChange={(_, value) => hadleWaterOptionChange("level", value)}
          />
          <Range
            label={`bumpHeight`}
            value={mapOptions.water.bumpHeight}
            max={2}
            min={0}
            step={0.05}
            onChange={(_, value) => hadleWaterOptionChange("bumpHeight", value)}
          />
          <Range
            label={`colorBlendFactor`}
            value={mapOptions.water.colorBlendFactor}
            max={1}
            min={0}
            step={0.05}
            onChange={(_, value) =>
              hadleWaterOptionChange("colorBlendFactor", value)
            }
          />
          <div className={styles.optionCheck}>
            <label htmlFor="waterColor">Water color</label>
            <input
              id="waterColor"
              type="color"
              value={rgbToHex(...mapOptions.water.waterColor)}
              onChange={(e) =>
                hadleWaterOptionChange("waterColor", hexToRgb(e.target.value))
              }
            ></input>
          </div>
          <Range
            label={`waveHeight`}
            value={mapOptions.water.waveHeight}
            max={5}
            min={0}
            step={0.1}
            onChange={(_, value) => hadleWaterOptionChange("waveHeight", value)}
          />
          <Range
            label={`waveLength`}
            value={mapOptions.water.waveLength}
            max={5}
            min={0.05}
            step={0.1}
            onChange={(_, value) => hadleWaterOptionChange("waveLength", value)}
          />
          <Range
            label={`windForce`}
            value={mapOptions.water.windForce}
            max={5}
            min={0}
            step={0.1}
            onChange={(_, value) => hadleWaterOptionChange("windForce", value)}
          />
          <Range
            label={`waveCount`}
            value={mapOptions.water.waveCount}
            max={100}
            min={0}
            step={25}
            onChange={(_, value) => hadleWaterOptionChange("waveCount", value)}
          />
        </OptionsGroup>

        <OptionsGroup label="Erosion">
          <div className={styles.optionCheck}>
            <label htmlFor="erosion">Do erosion</label>
            <input
              id="erosion"
              type="checkbox"
              checked={generateOptions.doErosion}
              onChange={(e) => handleGenerateOptionCheck("doErosion", e)}
            />
          </div>
          <Range
            label="erosionScale"
            value={generateOptions.erosionScale}
            max={400}
            min={-400}
            step={1}
            onChange={handleGeneratorRangeChange}
          />
          <OptionsGroup label="Vectors">
            {defaultGenerateOptions.erosionVectors.map(
              (initialValue, index) => (
                <React.Fragment key={`erosionVector${index}`}>
                  <Range
                    label={`erosionVecX${index}`}
                    value={generateOptions.erosionVectors[index].x}
                    max={100}
                    min={-100}
                    step={0.1}
                    onChange={(_, value) =>
                      handleVectorChange(index, "x", value)
                    }
                  />
                  <Range
                    label={`erosionVecY${index}`}
                    value={generateOptions.erosionVectors[index].y}
                    max={100}
                    min={-100}
                    step={0.1}
                    onChange={(_, value) =>
                      handleVectorChange(index, "y", value)
                    }
                  />
                </React.Fragment>
              )
            )}
          </OptionsGroup>
        </OptionsGroup>
        <OptionsGroup label="Textures" defaultOpen={true}>
          <Button onClick={setRandomTextures}>Set random textures</Button>
          <OptionsGroup label="UV's">
            {mapOptions.textures.map((texture, index) => (
              <div
                key={`${texture.name} ${index}`}
                className={styles.textureSettings}
              >
                <img
                  width={48}
                  height={48}
                  src={`textures/${texture.name}.jpg`}
                ></img>
                <div className={styles.availableTextures}>
                  {availableTextures.map((availTexture) => (
                    <img
                      key={`available texture ${availTexture}`}
                      className={cx(styles.textureIcon)}
                      width={64}
                      height={64}
                      onClick={() => handleTextureChange(index, availTexture)}
                      src={`textures/${availTexture}.jpg`}
                    ></img>
                  ))}
                </div>
                <Range
                  label={`${texture.name}`}
                  max={50}
                  min={0}
                  value={texture.uv || 50}
                  onChange={(_, value) => handleTextureUVChange(index, value)}
                  step={1}
                />
              </div>
            ))}
          </OptionsGroup>
          <OptionsGroup label="Levels">
            {mapOptions.levels.map((level, index) => (
              <Range
                key={`Level ${index}`}
                label={`Level ${index}`}
                max={mapOptions.levels[index + 1] || 1}
                min={mapOptions.levels[index - 1] || 0}
                value={mapOptions.levels[index]}
                onChange={(_, value) => handleTextureLevelChange(index, value)}
                step={0.01}
              />
            ))}
          </OptionsGroup>
        </OptionsGroup>

        <OptionsGroup label="Terrain settings">
          <Range
            label={`heightScale`}
            max={50}
            min={1}
            value={mapOptions.heightScale}
            onChange={handleMapOptionRangeChange}
            step={1}
          />
          <Range
            label={`mapSize`}
            max={500}
            min={10}
            value={mapSize}
            onChange={(_, value) => setMapSize(value)}
            step={1}
          />
        </OptionsGroup>
      </div>
      {/* <Button
        type="primary"
        onClick={async () => {
          await generateNoiseWithAI(mapType, getNoise);
          generateMixMap(
            mixMapCanvasRef.current,
            noiseCanvasRef.current,
            mapOptions.levels
          );
          redrawTerrain();
        }}
      >
        Generate noise with AI
      </Button> */}
      <Button onClick={() => fileInputRef?.current?.click()}>
        <input
          ref={fileInputRef}
          id="loadFile"
          type="file"
          onChange={loadImage}
          hidden
        />
        Load heightmap
      </Button>

      <Button onClick={clearNoise}>Clear noise</Button>
    </div>
  );
}
